import React, { useEffect, useState } from "react";
import { message, Popconfirm, Spin } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import mineCraftApi from "../../../../api/mineCraftApi";
import minecraftUrls from "../../../../configs/mine_craft/minecraftUrls";

interface CatalogAvatar {
  id: number;
  user_id: number | null;
  image_url: string;
}

interface PropsInterface {
  appId?: number;
  userId?: number;
  value?: number;
  onChange?: (id: number | undefined) => void;
}

// Uses local component state instead of the shared `avatarsSlice` because:
//   1. The picker's query is specialized — it asks for the union of the ops
//      gallery plus this specific user's personal uploads. The slice already
//      serves the broader `/avatars` listing page; mixing both result sets
//      into a single slice churns subscribers (including `usersSlice` via
//      the embedded `user` schema) and causes cascading re-renders in the
//      surrounding Edit drawer / Form.
//   2. The picker is throwaway data — it only lives while the drawer is
//      open and never needs to participate in cross-page state.
const AvatarPicker = ({ appId, userId, value, onChange }: PropsInterface) => {
  const [avatars, setAvatars] = useState<CatalogAvatar[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!appId) return;

    let cancelled = false;
    setLoading(true);

    mineCraftApi
      .get('/onesky/avatars', {
        params: { app_id: appId, user_id: userId, limit: 250, order_by: 'order', order: 'asc' }
      })
      .then(response => {
        if (cancelled) return;
        const list: CatalogAvatar[] = (response.data?.avatars || []).map((a: any) => ({
          id: a.id,
          user_id: a.user_id ?? null,
          image_url: minecraftUrls.remoteUrl(a.image_url),
        }));
        setAvatars(list);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [appId, userId]);

  async function handleDestroy(id: number) {
    try {
      await mineCraftApi.delete(`/onesky/avatars/${id}`);
      setAvatars(prev => prev.filter(a => a.id !== id));
      // If the deleted avatar was the current selection, the backend
      // reassigned the user to a fallback Avatar. Clear the form field
      // so the next submit doesn't reference a now-missing id.
      if (value === id) onChange?.(undefined);
      message.success(`ItemID ${id} was successful deleted`);
    } catch (error: any) {
      message.error(error?.response?.data?.[0] || error?.message || 'Failed to delete avatar');
    }
  }

  if (loading) {
    return <Spin />;
  }

  return (
    <div className='v-avatar-picker'>
      {avatars.map(avatar => {
        const isPersonal = !!avatar.user_id;
        const classes = [
          'v-avatar-picker__item',
          value === avatar.id ? 'v-avatar-picker__item--selected' : '',
        ].filter(Boolean).join(' ');

        return (
          <div key={avatar.id} className={classes}>
            <button
              type='button'
              className='v-avatar-picker__select'
              title={isPersonal ? 'Personal upload' : 'App catalog'}
              onClick={() => onChange?.(avatar.id)}
            >
              <img src={avatar.image_url} alt={`avatar-${avatar.id}`} />
            </button>

            {isPersonal && (
              <Popconfirm
                placement='bottomRight'
                title='Are you sure to delete this avatar?'
                onConfirm={() => handleDestroy(avatar.id)}
                okText='Yes'
                cancelText='No'
              >
                <button
                  type='button'
                  className='v-avatar-picker__destroy'
                  title='Delete personal avatar'
                  onClick={(e) => e.stopPropagation()}
                >
                  <DeleteOutlined />
                </button>
              </Popconfirm>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default AvatarPicker;
